import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import {
    Box, Typography, Tabs, Tab, Badge, IconButton, Tooltip,
} from '@mui/material';
import {
    Dashboard as OverviewIcon,
    FormatListBulleted as AllListIcon,
    BarChart as AnalyticsIcon,
    Refresh as RefreshIcon,
} from '@mui/icons-material';
import TeamTimesheetList from './teamTimesheetList';
import TimesheetAnalytics from './timesheetAnalytics';
import ManagerTimesheetOverview from './managerTimesheetOverview';
import { getData, postData } from '../../../services/axiosInstance';
import { API_ENDPOINTS } from '../../../services/endpoints';
import Loader from '../../../common/spinner';

// ─── Mock Data ────────────────────────────────────────────────────────────────
// TODO: replace with API call when backend is ready

export const MOCK_TEAM_MEMBERS = [
    { id: 'u1', name: 'Priya Sharma',  role: 'Frontend Developer', dept: 'Engineering', avatar: 'PS' },
    { id: 'u2', name: 'Arjun Mehta',   role: 'Backend Developer',  dept: 'Engineering', avatar: 'AM' },
    { id: 'u3', name: 'Sneha Reddy',   role: 'QA Engineer',        dept: 'Engineering', avatar: 'SR' },
    { id: 'u4', name: 'Kiran Patel',   role: 'UI/UX Designer',     dept: 'Design',      avatar: 'KP' },
    { id: 'u5', name: 'Divya Nair',    role: 'Business Analyst',   dept: 'Product',     avatar: 'DN' },
];

export const MOCK_TEAM_TIMESHEETS = [
    {
        id: 'mts-001', employeeId: 'u1', employeeName: 'Priya Sharma', employeeAvatar: 'PS',
        employeeRole: 'Frontend Developer', employeeDept: 'Engineering',
        employeeCode: 'EMP-001', managerName: 'Ravi Kumar',
        weekStart: '2026-05-18', weekEnd: '2026-05-24',
        totalHours: 42, overtimeHours: 2, billableHours: 38, projectCount: 2,
        status: 'Submitted', submittedOn: '2026-05-24',
        employeeComment: 'Sprint 14 week — completed auth module and started dashboard redesign.',
        level1ApproverComment: '', approvedBy: '', approvedOn: '',
        level2Required: false,
        approvalHistory: [],
        entries: [
            {
                date: '2026-05-18', day: 'Mon', project: 'Project Alpha', task: 'Development',
                description: 'Implemented JWT auth middleware', hours: 8, billable: 'Billable',
                location: 'Office', overtime: 0, remarks: '',
                attachments: [
                    {
                        name: 'auth-middleware-design.pdf', size: 312400, type: 'application/pdf',
                        url: 'https://www.w3.org/WAI/WCAG21/Techniques/pdf/pdf-sample.pdf',
                    },
                    {
                        name: 'jwt-flow-diagram.png', size: 87200, type: 'image/png',
                        url: 'https://upload.wikimedia.org/wikipedia/commons/thumb/4/47/PNG_transparency_demonstration_1.png/280px-PNG_transparency_demonstration_1.png',
                    },
                ],
            },
            {
                date: '2026-05-19', day: 'Tue', project: 'Project Alpha', task: 'Code Review',
                description: 'Reviewed PR #47 and #48 for auth changes', hours: 8, billable: 'Billable',
                location: 'Office', overtime: 0, remarks: '',
                attachments: [],
            },
            {
                date: '2026-05-20', day: 'Wed', project: 'Project Beta', task: 'UI/UX Design',
                description: 'Dashboard wireframe implementation', hours: 8, billable: 'Billable',
                location: 'Remote', overtime: 1, remarks: 'Worked late to meet deadline',
                attachments: [
                    {
                        name: 'dashboard-wireframe-v2.pdf', size: 1048576, type: 'application/pdf',
                        url: 'https://www.w3.org/WAI/WCAG21/Techniques/pdf/pdf-sample.pdf',
                    },
                    {
                        name: 'component-specs.xlsx', size: 204800, type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                        url: 'https://file-examples.com/storage/fe9b2a1f8362778134a3f21/2017/02/file_example_XLS_10.xls',
                    },
                ],
            },
            {
                date: '2026-05-21', day: 'Thu', project: 'Project Alpha', task: 'Development',
                description: 'Token refresh and session management', hours: 8, billable: 'Billable',
                location: 'Office', overtime: 0, remarks: '',
                attachments: [],
            },
            {
                date: '2026-05-22', day: 'Fri', project: 'Project Beta', task: 'Testing & QA',
                description: 'Integration tests for dashboard', hours: 8, billable: 'Non-Billable',
                location: 'Office', overtime: 1, remarks: '',
                attachments: [
                    {
                        name: 'test-results-fri.png', size: 143000, type: 'image/png',
                        url: 'https://upload.wikimedia.org/wikipedia/commons/thumb/4/47/PNG_transparency_demonstration_1.png/280px-PNG_transparency_demonstration_1.png',
                    },
                ],
            },
        ],
        // versionHistory: [ ... ],  // Phase 2
    },
    {
        id: 'mts-002', employeeId: 'u2', employeeName: 'Arjun Mehta', employeeAvatar: 'AM',
        employeeRole: 'Backend Developer', employeeDept: 'Engineering',
        employeeCode: 'EMP-002', managerName: 'Ravi Kumar',
        weekStart: '2026-05-18', weekEnd: '2026-05-24',
        totalHours: 40, overtimeHours: 0, billableHours: 40, projectCount: 1,
        status: 'Submitted', submittedOn: '2026-05-23',
        employeeComment: 'API development week.',
        level1ApproverComment: '', approvedBy: '', approvedOn: '',
        level2Required: false,
        approvalHistory: [],
        entries: [
            { date: '2026-05-18', day: 'Mon', project: 'Project Alpha', task: 'Development', description: 'REST API endpoints for timesheet module', hours: 8, billable: 'Billable', location: 'Office', overtime: 0, remarks: '', attachments: [] },
            { date: '2026-05-19', day: 'Tue', project: 'Project Alpha', task: 'Development', description: 'Database schema and migrations', hours: 8, billable: 'Billable', location: 'Office', overtime: 0, remarks: '', attachments: [] },
            { date: '2026-05-20', day: 'Wed', project: 'Project Alpha', task: 'Documentation', description: 'API documentation using Swagger', hours: 8, billable: 'Billable', location: 'Remote', overtime: 0, remarks: '',
              attachments: [
                {
                    name: 'swagger-api-docs-v1.pdf', size: 456000, type: 'application/pdf',
                    url: 'https://www.w3.org/WAI/WCAG21/Techniques/pdf/pdf-sample.pdf',
                },
              ],
            },
            { date: '2026-05-21', day: 'Thu', project: 'Project Alpha', task: 'Development', description: 'Unit tests for API layer', hours: 8, billable: 'Billable', location: 'Office', overtime: 0, remarks: '', attachments: [] },
            { date: '2026-05-22', day: 'Fri', project: 'Project Alpha', task: 'Sprint Planning Meeting', description: 'Sprint 15 planning and grooming', hours: 8, billable: 'Billable', location: 'Office', overtime: 0, remarks: '', attachments: [] },
        ],
        // versionHistory: [ ... ],  // Phase 2
    },
    {
        id: 'mts-003', employeeId: 'u3', employeeName: 'Sneha Reddy', employeeAvatar: 'SR',
        employeeRole: 'QA Engineer', employeeDept: 'Engineering',
        employeeCode: 'EMP-003', managerName: 'Ravi Kumar',
        weekStart: '2026-05-18', weekEnd: '2026-05-24',
        totalHours: 38, overtimeHours: 0, billableHours: 32, projectCount: 2,
        status: 'Submitted', submittedOn: '2026-05-24',
        employeeComment: '',
        level1ApproverComment: '', approvedBy: '', approvedOn: '',
        level2Required: true,
        approvalHistory: [],
        entries: [
            { date: '2026-05-18', day: 'Mon', project: 'Project Alpha', task: 'Testing & QA', description: 'Regression test suite execution', hours: 8, billable: 'Billable', location: 'Office', overtime: 0, remarks: '', attachments: [] },
            { date: '2026-05-19', day: 'Tue', project: 'Project Beta',  task: 'Testing & QA', description: 'UAT preparation and test cases', hours: 8, billable: 'Billable', location: 'Office', overtime: 0, remarks: '', attachments: [] },
            { date: '2026-05-20', day: 'Wed', project: 'Project Alpha', task: 'Bug Fix', description: 'Bug triage and reporting', hours: 6, billable: 'Billable', location: 'Remote', overtime: 0, remarks: 'Short day - doctor appointment',
              attachments: [
                {
                    name: 'bug-triage-report.pdf', size: 228000, type: 'application/pdf',
                    url: 'https://www.w3.org/WAI/WCAG21/Techniques/pdf/pdf-sample.pdf',
                },
              ],
            },
            { date: '2026-05-21', day: 'Thu', project: 'Project Alpha', task: 'Documentation', description: 'Test coverage reports', hours: 8, billable: 'Non-Billable', location: 'Office', overtime: 0, remarks: '', attachments: [] },
            { date: '2026-05-22', day: 'Fri', project: 'Project Beta',  task: 'Testing & QA', description: 'Performance testing', hours: 8, billable: 'Non-Billable', location: 'Office', overtime: 0, remarks: '', attachments: [] },
        ],
    },
    {
        id: 'mts-004', employeeId: 'u4', employeeName: 'Kiran Patel', employeeAvatar: 'KP',
        employeeRole: 'UI/UX Designer', employeeDept: 'Design',
        employeeCode: 'EMP-004', managerName: 'Ravi Kumar',
        weekStart: '2026-05-11', weekEnd: '2026-05-17',
        totalHours: 40, overtimeHours: 0, billableHours: 36, projectCount: 2,
        status: 'Approved', submittedOn: '2026-05-17',
        employeeComment: 'Design sprint week.',
        level1ApproverComment: 'Clean work, good attention to detail on the dashboard mockups.',
        approvedBy: 'Ravi Kumar', approvedOn: '2026-05-18',
        level2Required: false,
        approvalHistory: [
            { actor: 'Ravi Kumar', action: 'Approved', comment: 'Clean work, good attention to detail on the dashboard mockups.', timestamp: '2026-05-18 09:30' },
        ],
        entries: [
            { date: '2026-05-11', day: 'Mon', project: 'Project Beta',  task: 'UI/UX Design', description: 'Hi-fi mockups for client portal', hours: 8, billable: 'Billable', location: 'Office', overtime: 0, remarks: '',
              attachments: [
                {
                    name: 'client-portal-hifi-v1.pdf', size: 2097152, type: 'application/pdf',
                    url: 'https://www.w3.org/WAI/WCAG21/Techniques/pdf/pdf-sample.pdf',
                },
                {
                    name: 'mockup-preview.png', size: 512000, type: 'image/png',
                    url: 'https://upload.wikimedia.org/wikipedia/commons/thumb/4/47/PNG_transparency_demonstration_1.png/280px-PNG_transparency_demonstration_1.png',
                },
              ],
            },
            { date: '2026-05-12', day: 'Tue', project: 'Project Beta',  task: 'UI/UX Design', description: 'Component library update', hours: 8, billable: 'Billable', location: 'Remote', overtime: 0, remarks: '', attachments: [] },
            { date: '2026-05-13', day: 'Wed', project: 'Project Alpha', task: 'UI/UX Design', description: 'HRMS employee profile redesign', hours: 8, billable: 'Billable', location: 'Office', overtime: 0, remarks: '', attachments: [] },
            { date: '2026-05-14', day: 'Thu', project: 'Project Alpha', task: 'Sprint Planning Meeting', description: 'Design sprint review', hours: 8, billable: 'Billable', location: 'Office', overtime: 0, remarks: '', attachments: [] },
            { date: '2026-05-15', day: 'Fri', project: 'Project Beta',  task: 'Documentation', description: 'Design system documentation', hours: 8, billable: 'Non-Billable', location: 'Office', overtime: 0, remarks: '', attachments: [] },
        ],
    },
    {
        id: 'mts-005', employeeId: 'u5', employeeName: 'Divya Nair', employeeAvatar: 'DN',
        employeeRole: 'Business Analyst', employeeDept: 'Product',
        employeeCode: 'EMP-005', managerName: 'Ravi Kumar',
        weekStart: '2026-05-11', weekEnd: '2026-05-17',
        totalHours: 40, overtimeHours: 0, billableHours: 40, projectCount: 3,
        status: 'Approved', submittedOn: '2026-05-16',
        employeeComment: 'Requirements gathering week for Sprint 15.',
        level1ApproverComment: 'All documented well.',
        approvedBy: 'Ravi Kumar', approvedOn: '2026-05-17',
        level2Required: false,
        approvalHistory: [
            { actor: 'Ravi Kumar', action: 'Approved', comment: 'All documented well.', timestamp: '2026-05-17 10:00' },
        ],
        entries: [
            { date: '2026-05-11', day: 'Mon', project: 'Project Alpha', task: 'Documentation', description: 'BRD for timesheet module', hours: 8, billable: 'Billable', location: 'Office', overtime: 0, remarks: '',
              attachments: [
                {
                    name: 'BRD-timesheet-module-v2.pdf', size: 780000, type: 'application/pdf',
                    url: 'https://www.w3.org/WAI/WCAG21/Techniques/pdf/pdf-sample.pdf',
                },
              ],
            },
            { date: '2026-05-12', day: 'Tue', project: 'Project Beta',  task: 'Sprint Planning Meeting', description: 'Stakeholder requirements session', hours: 8, billable: 'Billable', location: 'Client Site', overtime: 0, remarks: '', attachments: [] },
            { date: '2026-05-13', day: 'Wed', project: 'Project Gamma', task: 'Documentation', description: 'Mobile app feature specs', hours: 8, billable: 'Billable', location: 'Office', overtime: 0, remarks: '', attachments: [] },
            { date: '2026-05-14', day: 'Thu', project: 'Project Alpha', task: 'Development', description: 'Process flow diagrams', hours: 8, billable: 'Billable', location: 'Office', overtime: 0, remarks: '', attachments: [] },
            { date: '2026-05-15', day: 'Fri', project: 'Project Gamma', task: 'Sprint Planning Meeting', description: 'Sprint 15 backlog grooming', hours: 8, billable: 'Billable', location: 'Office', overtime: 0, remarks: '', attachments: [] },
        ],
    },
    {
        id: 'mts-006', employeeId: 'u1', employeeName: 'Priya Sharma', employeeAvatar: 'PS',
        employeeRole: 'Frontend Developer', employeeDept: 'Engineering',
        employeeCode: 'EMP-001', managerName: 'Ravi Kumar',
        weekStart: '2026-05-11', weekEnd: '2026-05-17',
        totalHours: 40, overtimeHours: 0, billableHours: 32, projectCount: 1,
        status: 'Rejected', submittedOn: '2026-05-17',
        employeeComment: '',
        level1ApproverComment: 'Hours do not match the tasks described. Please provide more detailed descriptions and correct the daily breakdown.',
        approvedBy: 'Ravi Kumar', approvedOn: '2026-05-18',
        level2Required: false,
        approvalHistory: [
            { actor: 'Ravi Kumar', action: 'Rejected', comment: 'Hours do not match the tasks described. Please provide more detailed descriptions and correct the daily breakdown.', timestamp: '2026-05-18 11:15' },
        ],
        entries: [
            { date: '2026-05-11', day: 'Mon', project: 'Project Alpha', task: 'Development', description: 'Worked on frontend', hours: 8, billable: 'Billable', location: 'Office', overtime: 0, remarks: '', attachments: [] },
            { date: '2026-05-12', day: 'Tue', project: 'Project Alpha', task: 'Development', description: 'More work', hours: 8, billable: 'Non-Billable', location: 'Office', overtime: 0, remarks: '', attachments: [] },
            { date: '2026-05-13', day: 'Wed', project: 'Project Alpha', task: 'Development', description: 'Continued', hours: 8, billable: 'Billable', location: 'Remote', overtime: 0, remarks: '', attachments: [] },
            { date: '2026-05-14', day: 'Thu', project: 'Project Alpha', task: 'Development', description: 'Tasks', hours: 8, billable: 'Billable', location: 'Office', overtime: 0, remarks: '', attachments: [] },
            { date: '2026-05-15', day: 'Fri', project: 'Project Alpha', task: 'Development', description: 'End of week', hours: 8, billable: 'Non-Billable', location: 'Office', overtime: 0, remarks: '', attachments: [] },
        ],
    },
    {
        id: 'mts-007', employeeId: 'u2', employeeName: 'Arjun Mehta', employeeAvatar: 'AM',
        employeeRole: 'Backend Developer', employeeDept: 'Engineering',
        employeeCode: 'EMP-002', managerName: 'Ravi Kumar',
        weekStart: '2026-05-11', weekEnd: '2026-05-17',
        totalHours: 44, overtimeHours: 4, billableHours: 44, projectCount: 1,
        status: 'Approved', submittedOn: '2026-05-16',
        employeeComment: 'Overtime due to critical production bug on Wednesday.',
        level1ApproverComment: 'Great job handling the production incident.',
        approvedBy: 'Ravi Kumar', approvedOn: '2026-05-17',
        level2Required: false,
        approvalHistory: [
            { actor: 'Ravi Kumar', action: 'Approved', comment: 'Great job handling the production incident.', timestamp: '2026-05-17 08:45' },
        ],
        entries: [
            { date: '2026-05-11', day: 'Mon', project: 'Project Alpha', task: 'Development', description: 'Performance optimisation for query layer', hours: 8, billable: 'Billable', location: 'Office', overtime: 0, remarks: '', attachments: [] },
            { date: '2026-05-12', day: 'Tue', project: 'Project Alpha', task: 'Development', description: 'Redis caching implementation', hours: 8, billable: 'Billable', location: 'Office', overtime: 0, remarks: '', attachments: [] },
            {
                date: '2026-05-13', day: 'Wed', project: 'Project Alpha', task: 'Bug Fix',
                description: 'Production incident P1 — DB connection pool exhaustion', hours: 12, billable: 'Billable',
                location: 'Office', overtime: 4, remarks: 'Critical production fix',
                attachments: [
                    {
                        name: 'incident-report-P1.pdf', size: 524288, type: 'application/pdf',
                        url: 'https://www.w3.org/WAI/WCAG21/Techniques/pdf/pdf-sample.pdf',
                    },
                    {
                        name: 'db-error-screenshot.png', size: 95000, type: 'image/png',
                        url: 'https://upload.wikimedia.org/wikipedia/commons/thumb/4/47/PNG_transparency_demonstration_1.png/280px-PNG_transparency_demonstration_1.png',
                    },
                ],
            },
            {
                date: '2026-05-14', day: 'Thu', project: 'Project Alpha', task: 'Documentation',
                description: 'Post-mortem document', hours: 8, billable: 'Billable',
                location: 'Office', overtime: 0, remarks: '',
                attachments: [
                    {
                        name: 'postmortem-May13.pdf', size: 180000, type: 'application/pdf',
                        url: 'https://www.w3.org/WAI/WCAG21/Techniques/pdf/pdf-sample.pdf',
                    },
                ],
            },
            { date: '2026-05-15', day: 'Fri', project: 'Project Alpha', task: 'Development', description: 'Preventive monitoring setup', hours: 8, billable: 'Billable', location: 'Remote', overtime: 0, remarks: '', attachments: [] },
        ],
    },
    {
        id: 'mts-008', employeeId: 'u3', employeeName: 'Sneha Reddy', employeeAvatar: 'SR',
        employeeRole: 'QA Engineer', employeeDept: 'Engineering',
        employeeCode: 'EMP-003', managerName: 'Ravi Kumar',
        weekStart: '2026-05-04', weekEnd: '2026-05-10',
        totalHours: 40, overtimeHours: 0, billableHours: 40, projectCount: 2,
        status: 'Approved', submittedOn: '2026-05-10',
        employeeComment: 'UAT week for Sprint 13 release.',
        level1ApproverComment: 'Thorough testing coverage.',
        approvedBy: 'Ravi Kumar', approvedOn: '2026-05-11',
        level2Required: false,
        approvalHistory: [
            { actor: 'Ravi Kumar', action: 'Approved', comment: 'Thorough testing coverage.', timestamp: '2026-05-11 09:00' },
        ],
        entries: [
            { date: '2026-05-04', day: 'Mon', project: 'Project Alpha', task: 'Testing & QA', description: 'UAT execution — auth flows', hours: 8, billable: 'Billable', location: 'Office', overtime: 0, remarks: '', attachments: [] },
            { date: '2026-05-05', day: 'Tue', project: 'Project Beta',  task: 'Testing & QA', description: 'Client portal UAT', hours: 8, billable: 'Billable', location: 'Client Site', overtime: 0, remarks: '', attachments: [] },
            { date: '2026-05-06', day: 'Wed', project: 'Project Alpha', task: 'Bug Fix', description: 'Defect re-testing', hours: 8, billable: 'Billable', location: 'Office', overtime: 0, remarks: '', attachments: [] },
            { date: '2026-05-07', day: 'Thu', project: 'Project Beta',  task: 'Testing & QA', description: 'Sign-off test execution', hours: 8, billable: 'Billable', location: 'Office', overtime: 0, remarks: '',
              attachments: [
                {
                    name: 'uat-signoff-report.pdf', size: 340000, type: 'application/pdf',
                    url: 'https://www.w3.org/WAI/WCAG21/Techniques/pdf/pdf-sample.pdf',
                },
              ],
            },
            { date: '2026-05-08', day: 'Fri', project: 'Project Alpha', task: 'Documentation', description: 'Test sign-off report', hours: 8, billable: 'Billable', location: 'Office', overtime: 0, remarks: '', attachments: [] },
        ],
    },
    {
        id: 'mts-009', employeeId: 'u4', employeeName: 'Kiran Patel', employeeAvatar: 'KP',
        employeeRole: 'UI/UX Designer', employeeDept: 'Design',
        employeeCode: 'EMP-004', managerName: 'Ravi Kumar',
        weekStart: '2026-05-18', weekEnd: '2026-05-24',
        totalHours: 32, overtimeHours: 0, billableHours: 24, projectCount: 1,
        status: 'Submitted', submittedOn: '2026-05-24',
        employeeComment: 'Partial week — 2 days on leave.',
        level1ApproverComment: '', approvedBy: '', approvedOn: '',
        level2Required: false,
        approvalHistory: [],
        entries: [
            { date: '2026-05-18', day: 'Mon', project: 'Project Gamma', task: 'UI/UX Design', description: 'Mobile app onboarding screens', hours: 8, billable: 'Billable', location: 'Office', overtime: 0, remarks: '',
              attachments: [
                {
                    name: 'onboarding-screens-v1.png', size: 720000, type: 'image/png',
                    url: 'https://upload.wikimedia.org/wikipedia/commons/thumb/4/47/PNG_transparency_demonstration_1.png/280px-PNG_transparency_demonstration_1.png',
                },
              ],
            },
            { date: '2026-05-19', day: 'Tue', project: 'Project Gamma', task: 'UI/UX Design', description: 'Icon set and assets', hours: 8, billable: 'Billable', location: 'Office', overtime: 0, remarks: '', attachments: [] },
            { date: '2026-05-20', day: 'Wed', project: 'Project Gamma', task: 'UI/UX Design', description: 'Prototype for user testing', hours: 8, billable: 'Billable', location: 'Remote', overtime: 0, remarks: '', attachments: [] },
            { date: '2026-05-21', day: 'Thu', project: 'Project Gamma', task: 'Documentation', description: 'Design handoff notes', hours: 8, billable: 'Non-Billable', location: 'Office', overtime: 0, remarks: '', attachments: [] },
        ],
    },
    {
        id: 'mts-010', employeeId: 'u5', employeeName: 'Divya Nair', employeeAvatar: 'DN',
        employeeRole: 'Business Analyst', employeeDept: 'Product',
        employeeCode: 'EMP-005', managerName: 'Ravi Kumar',
        weekStart: '2026-05-18', weekEnd: '2026-05-24',
        totalHours: 12, overtimeHours: 0, billableHours: 12, projectCount: 1,
        status: 'Draft', submittedOn: '',
        employeeComment: '',
        level1ApproverComment: '', approvedBy: '', approvedOn: '',
        level2Required: false,
        approvalHistory: [],
        entries: [
            { date: '2026-05-18', day: 'Mon', project: 'Project Alpha', task: 'Sprint Planning Meeting', description: 'Sprint 15 kick-off', hours: 4, billable: 'Billable', location: 'Office', overtime: 0, remarks: '', attachments: [] },
            { date: '2026-05-19', day: 'Tue', project: 'Project Alpha', task: 'Documentation', description: 'Updated requirements doc', hours: 8, billable: 'Billable', location: 'Office', overtime: 0, remarks: '', attachments: [] },
        ],
    },
];

// ─── API Normalizers ──────────────────────────────────────────────────────────
const DAY_ABBR = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function normalizeEntry(e) {
    const dateStr = e.entry_date || e.date || '';
    const dayAbbr = dateStr ? DAY_ABBR[new Date(dateStr + 'T00:00:00').getDay()] : (e.day || '');

    let attachments = e.attachments || [];
    if (!attachments.length && e.attachment_urls) {
        const raw = e.attachment_urls;
        if (Array.isArray(raw)) attachments = raw;
        else if (typeof raw === 'string') { try { attachments = JSON.parse(raw); } catch { attachments = []; } }
    }

    return {
        ...e,
        date: dateStr,
        day: dayAbbr,
        dayType: e.day_type || e.dayType || 'Working',
        project: e.project_name || e.project || '',
        task: e.task_name || e.task || '',
        description: e.work_description || e.description || '',
        hours: parseFloat(e.hours_worked ?? e.hours ?? 0),
        overtime: parseFloat(e.overtime_hours ?? e.overtime ?? 0),
        billable: e.is_billable !== undefined ? (e.is_billable ? 'Billable' : 'Non-Billable') : (e.billable || 'Non-Billable'),
        location: e.location || e.work_location || '',
        leaveType: e.leave_type || e.leaveType || '',
        attachments,
    };
}

function normalizeTimesheet(ts) {
    return {
        ...ts,
        employeeDept: ts.department_name || ts.employeeDept || '',
        employeeTeam: ts.team_name || ts.employeeTeam || '',
        managerName: ts.level1_approver_name || ts.managerName || '',
        approvedBy: ts.level1_approver_name || ts.rejected_by_name || ts.approvedBy || '',
        approvedOn: ts.approvedOn || '',
        // versionHistory: ts.versionHistory || [],  // Phase 2
        entries: (ts.entries || []).map(normalizeEntry),
    };
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function ManagerTimesheets() {
    const location = useLocation();
    const [searchParams, setSearchParams] = useSearchParams();
    const [activeTab, setActiveTab]     = useState(() => {
        const fromUrl = parseInt(searchParams.get('tab'), 10);
        return Number.isInteger(fromUrl) ? fromUrl : (location.state?.returnTab ?? 0);
    });
    const [listFilter, setListFilter]   = useState('all');
    const [timesheets, setTimesheets]   = useState([]);
    const [teamMembers, setTeamMembers] = useState([]);
    const [loading, setLoading]         = useState(true);
    const [error, setError]             = useState(null);

    const handleTabChange = useCallback((tab, filter) => {
        setActiveTab(tab);
        setListFilter(filter !== undefined ? filter : 'all');
        setSearchParams(prev => {
            const next = new URLSearchParams(prev);
            next.set('tab', String(tab));
            return next;
        }, { replace: true });
    }, [setSearchParams]);

    const fetchTimesheets = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const url = API_ENDPOINTS.getTeamTimesheets('', '', '', 'all', '', 1, 500);
            const res = await getData(url);
            const raw = res?.result?.data || [];
            const normalized = raw.map(normalizeTimesheet);
            setTimesheets(normalized);

            const seen = {};
            normalized.forEach(ts => {
                const key = String(ts.employeeId);
                if (ts.employeeId && !seen[key]) {
                    seen[key] = { id: key, name: ts.employeeName, avatar: ts.employeeAvatar, role: ts.employeeRole, dept: ts.employeeDept };
                }
            });
            setTeamMembers(Object.values(seen));
        } catch (err) {
            console.error('Failed to fetch team timesheets:', err);
            setError('Failed to load timesheets. Please try again.');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { fetchTimesheets(); }, [fetchTimesheets]);

    const handleAction = useCallback(async (id, decision, comment) => {
        if (decision === 'SendToL2') {
            await fetchTimesheets();
            return;
        }
        const url = decision === 'Approve'
            ? API_ENDPOINTS.approveTimesheet(id)
            : API_ENDPOINTS.rejectTimesheet(id);
        await postData(url, { comment: comment || '' });
        await fetchTimesheets();
    }, [fetchTimesheets]);

    const handleBulkApprove = useCallback(async (ids) => {
        await postData(API_ENDPOINTS.bulkApproveTimesheets, { timesheet_ids: ids });
        await fetchTimesheets();
    }, [fetchTimesheets]);

    const handleBulkReject = useCallback(async (ids, comment) => {
        await postData(API_ENDPOINTS.bulkRejectTimesheets, { timesheet_ids: ids, comment });
        await fetchTimesheets();
    }, [fetchTimesheets]);

    const pendingCount = timesheets.filter(t => t.status === 'Submitted').length;

    if (loading) {
        return <Loader />
    }

    return (
        <Box>
            <Box sx={{ mb: 2.5, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                <Box>
                    <Typography variant="h5" sx={{ fontWeight: 700, color: '#1e293b', mb: 0.25 }}>
                        Team Timesheet Management
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#64748b' }}>
                        Review, approve, and track your team's weekly timesheets
                    </Typography>
                </Box>
                <Tooltip title="Refresh">
                    <IconButton onClick={fetchTimesheets} sx={{ border: '1px solid #e2e8f0', borderRadius: 2 }}>
                        <RefreshIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
            </Box>

            <Box sx={{ bgcolor: '#fff', borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.04)', border: '1px solid #e2e8f0', mb: 2.5, px: 0.5, py: 0.5 }}>
                <Tabs
                    value={activeTab}
                    onChange={(_, v) => handleTabChange(v)}
                    variant="scrollable"
                    scrollButtons="auto"
                    sx={{
                        minHeight: 44,
                        '& .MuiTabs-indicator': { display: 'none' },
                        '& .MuiTab-root': {
                            textTransform: 'none', fontWeight: 600, fontSize: 13,
                            minHeight: 40, borderRadius: 2, color: '#64748b',
                            '&.Mui-selected': { color: '#6366f1', bgcolor: '#eef2ff' },
                        },
                    }}
                >
                    <Tab icon={<OverviewIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="Overview" />
                    <Tab
                        icon={<AllListIcon sx={{ fontSize: 18 }} />}
                        iconPosition="start"
                        label={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                Team Timesheets
                                {pendingCount > 0 && (
                                    <Badge badgeContent={pendingCount} color="warning"
                                        sx={{ '& .MuiBadge-badge': { fontSize: 10, fontWeight: 700, minWidth: 18, height: 18 } }}>
                                        <Box sx={{ width: 4 }} />
                                    </Badge>
                                )}
                            </Box>
                        }
                    />
                    <Tab icon={<AnalyticsIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="Analytics" />
                </Tabs>
            </Box>

            {activeTab === 0 && <ManagerTimesheetOverview timesheets={timesheets} onTabChange={handleTabChange} />}
            {activeTab === 1 && (
                <TeamTimesheetList timesheets={timesheets} teamMembers={teamMembers} loading={loading}
                    filterStatus={listFilter} onAction={handleAction}
                    onBulkApprove={handleBulkApprove} onBulkReject={handleBulkReject} />
            )}
            {activeTab === 2 && <TimesheetAnalytics timesheets={timesheets} teamMembers={teamMembers} />}
        </Box>
    );
}
